"use client";

import * as React from "react";
import { ArrowRight, CircleCheck, Factory, Server, Stethoscope, Thermometer } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const clients = [{
  name: "Hospital Albert Einstein",
  logo: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/Siemens-logo.svg_.png?v=xshoac9wgif"
}, {
  name: "Rede D'Or",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/logo_rededor.png"
}, {
  name: "Equinix",
  logo: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/HSL_PH_rgb_pos.jpg?v=y8egir9l5w"
}, {
  name: "Eurofarma",
  logo: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/Nubank_logo.svg_.png?v=nzy9gcl3l59"
}, {
  name: "Fleury",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/logo_fleury.png"
}, {
  name: "Ascenty",
  logo: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/GE_HealthCare_logo_2023-scaled.png?v=7dll3z9drgd"
}];
const cases = [{
  client: "Hospital de Referência SP",
  image: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/MRI-scanner.jpg?v=t6tpqvjzp5",
  category: "Saúde / Hospitalar",
  icon: <Stethoscope className="size-5" />,
  challenge: "Controle rigoroso de umidade e temperatura para salas de Ressonância Magnética (RM), evitando condensação em equipamentos críticos.",
  solution: "Implementação de Chillers de Precisão com redundância N+1 e sistema de automação dedicado para monitoramento 24/7.",
  imageClassName: "absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 lg:hidden !bg-[url(https://doctorice.10web.cloud/wp-content/uploads/2026/04/revolution-apex-platform-_-ge-healthcare-2.jpg)] absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 lg:hidden !bg-[url(https://doctorice.10web.cloud/wp-content/uploads/2026/04/1747744958452.jpeg)] absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 lg:hidden !bg-[url(https://doctorice.10web.cloud/wp-content/uploads/2026/04/1747744958452.jpeg)] absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 lg:hidden !bg-[url(https://doctorice.10web.cloud/wp-content/uploads/2026/04/ge_3t.jpeg)]"
}, {
  client: "Datacenter Hyperscale",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/case_datacenter_cooling.png",
  category: "Telecom / Datacenter",
  icon: <Server className="size-5" />,
  challenge: "Dissipação de calor em corredores de alta densidade sem comprometer a eficiência energética (PUE).",
  solution: "Sistema de confinamento de corredor frio integrado a unidades de tratamento de ar de alta vazão e free-cooling indireto."
}, {
  client: "Indústria Farmacêutica",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/case_cleanroom_pharma.png",
  category: "Salas Limpas",
  icon: <Factory className="size-5" />,
  challenge: "Manutenção de diferenciais de pressão e filtragem absoluta (HEPA) para conformidade com normas ANVISA em área de envase.",
  solution: "Projeto turnkey de climatização com controle fino de umidade relativa e cascata de pressão automatizada."
}, {
  client: "Processo Industrial Alimentício",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/case_industrial_process.png",
  category: "Indústria",
  icon: <Thermometer className="size-5" />,
  challenge: "Estabilidade térmica em túneis de resfriamento para garantir a textura e qualidade final do produto.",
  solution: "Retrofit do sistema de água gelada com novos trocadores de calor e otimização hidráulica para redução de consumo."
}];
export default function ProjetosDeReferencia() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="projetos-de-referência" ref={ref} className="relative w-full py-20 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" aria-hidden="true">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="var(--primary)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="container relative mx-auto px-4 md:px-6 z-10">
        {}
        <div className={cn("mb-12 text-center transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 font-sans">
            Confiança em Aplicações Críticas
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Grandes nomes da saúde, tecnologia e indústria confiam na engenharia da Doctorice para proteger suas operações mais sensíveis.
          </p>
        </div>

        {}
        <div className={cn("grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-items-center mb-20 transition-all duration-1000 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          {clients.map((client, i) => <div key={i} data-index={i} className="group relative w-32 h-16 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-500 opacity-60 hover:opacity-100">
              {}
              <img src={client.logo} alt={`${client.name} logo`} className="max-w-full max-h-full object-contain" onError={e => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
              <span className="hidden text-sm font-semibold text-center text-muted-foreground group-hover:text-primary">
                {client.name}
              </span>
            </div>)}
        </div>

        {}
        <div className={cn("relative transition-all duration-1000 delay-400 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <span className="w-1 h-8 bg-primary rounded-full block" />
              Cases em Destaque
            </h3>
            <div className="hidden md:flex gap-2">
              {}
            </div>
          </div>

          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {cases.map((item, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:basis-1/1 lg:basis-1/1">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-border rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-md transition-shadow duration-300">
                    
                    {}
                    <div className="lg:col-span-7 relative h-64 lg:h-auto overflow-hidden group">
                      <div className={`${item.imageClassName || ""} absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 lg:hidden`} />
                      <img src={item.image} alt={`Projeto ${item.client}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute bottom-4 left-4 z-20 lg:hidden text-white">
                        <div className="flex items-center gap-2 text-sm font-medium mb-1 opacity-90">
                          {item.icon}
                          {item.category}
                        </div>
                        <p className="font-bold text-lg">{item.client}</p>
                      </div>
                    </div>

                    {}
                    <div className="lg:col-span-5 flex flex-col justify-center p-6 lg:p-10 bg-card relative">
                      {}
                      <div className="absolute top-6 right-6 text-border/40">
                        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="30" cy="30" r="29" stroke="currentColor" strokeWidth="1" />
                          <path d="M30 0V60 M0 30H60" stroke="currentColor" strokeWidth="1" />
                        </svg>
                      </div>

                      <div className="hidden lg:flex items-center gap-2 text-primary font-medium mb-4">
                        <span className="p-1.5 bg-primary/10 rounded-md">
                          {item.icon}
                        </span>
                        {item.category}
                      </div>

                      <h4 className="text-2xl font-bold mb-6 text-foreground hidden lg:block">
                        {item.client}
                      </h4>

                      <div className="space-y-6">
                        <div>
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-destructive" />
                            O Desafio
                          </p>
                          <p className="text-foreground/80 leading-relaxed">
                            {item.challenge}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            A Solução
                          </p>
                          <p className="text-foreground/80 leading-relaxed">
                            {item.solution}
                          </p>
                        </div>
                      </div>

                      <div className="mt-8 pt-6 border-t border-border">
                        <div className="flex items-center gap-2 text-sm font-medium text-primary">
                          <CircleCheck className="size-4" />
                          <span>Resultado validado e em operação</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="absolute -bottom-12 right-0 flex gap-2 lg:bottom-auto lg:top-[-4.5rem] lg:right-0">
              <CarouselPrevious className="static translate-y-0 translate-x-0 h-10 w-10 rounded-sm border border-border bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-2xs" variant="outline" />
              <CarouselNext className="static translate-y-0 translate-x-0 h-10 w-10 rounded-sm border border-border bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-2xs" variant="outline" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}